import { Moment } from 'moment-timezone';
import { isValid, unixToDate } from 'app/shared/utilities/time';
import moment from 'moment-timezone';
import { head } from 'lodash';

/**
 * All possible date time formats supported by the timestamp formatter.
 * The more formats we have around the app, the less consistent the user experience!
 * So please reuse wherever possible, or at least have a good reason to add more formats
 */
export enum Format {
    ISO_8601, // 2018-08-24T02:32:20+10:00
    ISO_8601_WITH_SPACE, // 2018-08-24 02:32:20+10:00
    LONG_DATE_TIME, // Aug 24, 2018, 02:32:20 GMT+10
    LONG_DATE_TIME_NO_TZ, // August 24, 2018 02:32
    SHORT_DATE_TIME, // Aug 24, 02:32:20
    JUST_DATE, // August 24, 2018
    JUST_DATE_SHORT, // 24 Aug, 2018
    JUST_TIME, // 02:32:20
    JUST_TIME_HH_MM, // 02:32
}

interface Formatter {
    formatMoment(moment: Moment): string;
    formatDate(date: Date): string;
    formatUnix(unix: number): string;
}

const INVALID_DATE = 'None';

class IntlDateFormatter implements Formatter {
    private readonly _formatter: Intl.DateTimeFormat;

    constructor(options: Intl.DateTimeFormatOptions) {
        this._formatter = new Intl.DateTimeFormat('en', options);
    }

    formatDate(date: Date): string {
        return isValid(date) ? this._formatter.format(date) : INVALID_DATE;
    }

    formatMoment(moment: moment.Moment): string {
        const date = moment.toDate();
        return isValid(date) ? this._formatter.format(date) : INVALID_DATE;
    }

    formatUnix(unix: number): string {
        const date = unixToDate(unix);
        return isValid(date) ? this._formatter.format(date) : INVALID_DATE;
    }
}

class MomentDateFormatter implements Formatter {
    private readonly _formatString: string;
    private readonly _timeZone: string;

    /**
     * A {@link Formatter} implementation which makes uses of moments formatter.
     * Less preferable to the {@link IntlDateFormatter} as moments are far slower to create and format
     * However, in some cases we need fine control of the timestamp output (like ISO8601)
     * @param timeZone The timezone this formatter works with
     * @param formatString The moment compatible format string
     */
    constructor(timeZone: string, formatString: string) {
        this._formatString = formatString;
        this._timeZone = timeZone;
    }

    formatDate(date: Date): string {
        return isValid(date)
            ? moment.tz(date, this._timeZone).format(this._formatString)
            : INVALID_DATE;
    }

    formatMoment(moment: moment.Moment): string {
        return moment && moment.isValid()
            ? moment.clone().tz(this._timeZone).format(this._formatString)
            : INVALID_DATE;
    }

    formatUnix(unix: number): string {
        const mo = moment.tz(unix * 1000, this._timeZone);
        return mo.isValid() ? mo.format(this._formatString) : INVALID_DATE;
    }
}

function createFormatterForOption(timeZone: string, format: Format): Formatter {
    switch (format) {
        case Format.ISO_8601: {
            // Currently Intl.DateTimeFormatter can't handle ISO type formats, so we fallback to moment!
            return new MomentDateFormatter(timeZone, 'YYYY-MM-DDTHH:mm:ssZ');
        }
        case Format.ISO_8601_WITH_SPACE: {
            // Currently Intl.DateTimeFormatter can't handle ISO type formats, so we fallback to moment!
            return new MomentDateFormatter(timeZone, 'YYYY-MM-DD HH:mm:ssZ');
        }
        case Format.LONG_DATE_TIME: {
            return new IntlDateFormatter({
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZoneName: 'short',
                hourCycle: 'h23',
                timeZone,
            } as any);
        }
        case Format.LONG_DATE_TIME_NO_TZ: {
            return new IntlDateFormatter({
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hourCycle: 'h23',
                timeZone,
            } as any);
        }
        case Format.SHORT_DATE_TIME: {
            return new IntlDateFormatter({
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hourCycle: 'h23',
                timeZone,
            } as any);
        }
        case Format.JUST_DATE: {
            return new IntlDateFormatter({
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hourCycle: 'h23',
                timeZone,
            } as any);
        }
        case Format.JUST_DATE_SHORT: {
            return new IntlDateFormatter({
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hourCycle: 'h23',
                timeZone,
            } as any);
        }
        case Format.JUST_TIME: {
            return new IntlDateFormatter({
                hour: '2-digit',
                second: '2-digit',
                minute: '2-digit',
                hourCycle: 'h23',
                timeZone,
            } as any);
        }
        case Format.JUST_TIME_HH_MM: {
            return new IntlDateFormatter({
                hour: '2-digit',
                minute: '2-digit',
                hourCycle: 'h23',
                timeZone,
            } as any);
        }
    }
    throw new Error(`Can't handle format ${format}`);
}

export class CachedTimestampFormatter {
    private readonly _cache: Map<number, Map<string, Formatter>>;
    private readonly _cacheSize: number;
    private readonly _formatterFactory: (timeZone: string, format: Format) => any;

    /**
     * A basic timestamp formatter.
     * Allows formatting all types of date objects into strings.
     * A stateful object as it does some caching to make things faster.
     * Also uses predefined formats for both UX consistency and performance
     *
     * The constructor args are only really available for testing
     * @param cacheSize The maximum number of {@link Formatter} instances cached for each {@link Format}, per time zone
     * @param formatterFactory A function which creates a {@link Formatter} object based on a {@link Format} and time zone
     */
    constructor(
        cacheSize: number = 10,
        formatterFactory: (
            timeZone: string,
            format: Format,
        ) => Formatter = createFormatterForOption,
    ) {
        this._cacheSize = cacheSize;
        this._formatterFactory = formatterFactory;
        this._cache = new Map();
    }

    private _getFormatter(timeZone: string, format: Format): Formatter {
        const cacheByTimeZone = this._cache.get(format);
        // handle the fast path first!
        if (cacheByTimeZone && cacheByTimeZone.has(timeZone)) {
            return cacheByTimeZone.get(timeZone);
        }
        // formatter is not in the cache, make a new one
        const formatter = this._formatterFactory(timeZone, format);
        if (!cacheByTimeZone) {
            // if it's the first zone for this format, make a new map with the zone formatter
            this._cache.set(format, new Map([[timeZone, formatter]]));
            return formatter;
        }
        // cache the formatter for later
        cacheByTimeZone.set(timeZone, formatter);
        // do we need to clean up the cache?
        if (cacheByTimeZone.size > this._cacheSize) {
            // entries() gives us an iterator of the map [key value] pairs,
            // which we turn into an array and take the first one.
            // According to the spec it should be in insertion order,
            // meaning the first item should be the first inserted; therefore the oldest.
            const oldestEntry = head(Array.from(cacheByTimeZone.entries()))[0];
            cacheByTimeZone.delete(oldestEntry);
        }
        return formatter;
    }

    formatUnix(unix: number, timeZone: string, format: Format = Format.LONG_DATE_TIME): string {
        return this._getFormatter(timeZone, format).formatUnix(unix);
    }

    formatDate(date: Date, timeZone: string, format: Format = Format.LONG_DATE_TIME): string {
        return this._getFormatter(timeZone, format).formatDate(date);
    }

    formatMoment(moment: Moment, timeZone: string, format: Format = Format.LONG_DATE_TIME): string {
        return this._getFormatter(timeZone, format).formatMoment(moment);
    }
}
